import { useState, useEffect, useCallback, useMemo } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/App";
import apiClient from "@/utils/apiClient";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { 
  Smile, Meh, Frown, CloudRain, Sun, Heart, 
  AlertTriangle, Coffee, Moon, Dumbbell, Users,
  Music, Book, CheckCircle
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";

const MOOD_OPTIONS = [
  { value: 1, label: "Very Low", icon: CloudRain, color: "text-blue-500" },
  { value: 3, label: "Low", icon: Frown, color: "text-indigo-500" },
  { value: 5, label: "Neutral", icon: Meh, color: "text-spa-text-secondary" },
  { value: 7, label: "Good", icon: Smile, color: "text-spa-sage" },
  { value: 10, label: "Great", icon: Sun, color: "text-spa-terracotta" },
];

const TRIGGER_OPTIONS = [
  { id: "work", label: "Work Stress", icon: AlertTriangle },
  { id: "relationships", label: "Relationships", icon: Heart },
  { id: "health", label: "Health", icon: Dumbbell },
  { id: "sleep", label: "Sleep Issues", icon: Moon },
  { id: "social", label: "Social", icon: Users },
];

const ACTIVITY_OPTIONS = [
  { id: "exercise", label: "Exercise", icon: Dumbbell },
  { id: "meditation", label: "Meditation", icon: Coffee },
  { id: "social", label: "Social Time", icon: Users },
  { id: "music", label: "Music", icon: Music },
  { id: "reading", label: "Reading", icon: Book },
];

const CHART_GRADIENT = (
  <defs>
    <linearGradient id="moodGrad" x1="0" y1="0" x2="0" y2="1">
      <stop offset="5%" stopColor="#C5705D" stopOpacity={0.3}/>
      <stop offset="95%" stopColor="#C5705D" stopOpacity={0}/>
    </linearGradient>
  </defs>
);

const AXIS_TICK_STYLE = { fill: '#5F6D5B', fontSize: 10 };

export default function MoodTracker() {
  const { user } = useAuth();
  const [moodScore, setMoodScore] = useState(5);
  const [journalEntry, setJournalEntry] = useState("");
  const [selectedTriggers, setSelectedTriggers] = useState([]);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const fetchEntries = useCallback(async () => {
    try {
      const response = await apiClient.get('/mood');
      setEntries(response.data);
    } catch (error) {
      // silent
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await apiClient.get('/mood/stats');
      setStats(response.data);
    } catch (error) {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchEntries();
    fetchStats();
  }, [fetchEntries, fetchStats]);

  const getMoodLabel = (score) => {
    if (score <= 2) return "very_low";
    if (score <= 4) return "low";
    if (score <= 6) return "neutral";
    if (score <= 8) return "good";
    return "great";
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await apiClient.post('/mood', {
        mood_score: moodScore,
        mood_label: getMoodLabel(moodScore),
        journal_entry: journalEntry,
        triggers: selectedTriggers,
        activities: selectedActivities
      });

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      
      setMoodScore(5);
      setJournalEntry("");
      setSelectedTriggers([]);
      setSelectedActivities([]);
      
      fetchEntries();
      fetchStats();
    } catch (error) {
      // silent
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleTrigger = (id) => {
    setSelectedTriggers(prev => 
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
    );
  };

  const toggleActivity = (id) => {
    setSelectedActivities(prev => 
      prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    );
  };

  const chartData = useMemo(() => {
    return entries.slice(0, 14).reverse().map((entry) => ({
      day: new Date(entry.created_at).toLocaleDateString('en-US', { weekday: 'short' }),
      mood: entry.mood_score
    }));
  }, [entries]);

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 lg:p-12 space-y-8 animate-fade-in" data-testid="mood-tracker-page">
        {/* Success Message */}
        {showSuccess && (
          <div className="fixed top-4 right-4 z-50 bg-spa-sage text-white px-6 py-3 rounded-xl flex items-center gap-2 shadow-lg animate-fade-in">
            <CheckCircle className="w-5 h-5" />
            <span>Mood entry saved! +5 SMAHOO Coins</span>
          </div>
        )}

        {/* Header */}
        <div>
          <p className="overline-text mb-2">Track Your Journey</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-light text-spa-moss mb-3">
            Mood Tracker & Journal
          </h1>
          <p className="text-spa-text-secondary max-w-2xl">
            Understanding your emotional patterns helps build resilience. 
            Track your mood daily to gain insights and earn SMAHOO Coins.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Mood Entry Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Mood Slider */}
            <div className="spa-card" data-testid="mood-slider-card">
              <h2 className="font-serif text-xl text-spa-moss mb-6">How are you feeling?</h2>
              
              <div className="flex justify-between mb-4">
                {MOOD_OPTIONS.map((option) => {
                  const isActive = Math.round(moodScore / 2) * 2 === option.value || 
                    (moodScore >= option.value - 1 && moodScore <= option.value + 1);
                  return (
                    <button
                      key={option.value}
                      onClick={() => setMoodScore(option.value)}
                      className={`flex flex-col items-center p-3 rounded-xl transition-all ${
                        isActive ? 'bg-spa-muted scale-110' : 'hover:bg-spa-muted/50'
                      }`}
                      data-testid={`mood-option-${option.value}`}
                    >
                      <option.icon className={`w-8 h-8 ${option.color} ${isActive ? 'scale-110' : ''}`} />
                      <span className={`text-xs mt-2 ${isActive ? 'text-spa-moss font-medium' : 'text-spa-text-secondary'}`}>
                        {option.label}
                      </span>
                    </button>
                  );
                })}
              </div>

              <Slider
                value={[moodScore]}
                onValueChange={([value]) => setMoodScore(value)}
                min={1}
                max={10}
                step={1}
                className="py-4"
                data-testid="mood-slider"
              />
              <div className="flex justify-between text-xs text-spa-text-secondary">
                <span>1</span>
                <span className="font-medium text-spa-moss text-lg">{moodScore}/10</span>
                <span>10</span>
              </div>
            </div>

            {/* Triggers */}
            <div className="spa-card" data-testid="triggers-card">
              <h2 className="font-serif text-xl text-spa-moss mb-4">Any triggers today?</h2>
              <div className="flex flex-wrap gap-2">
                {TRIGGER_OPTIONS.map((trigger) => (
                  <button
                    key={trigger.id}
                    onClick={() => toggleTrigger(trigger.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-colors ${
                      selectedTriggers.includes(trigger.id)
                        ? 'bg-spa-terracotta text-white border-spa-terracotta'
                        : 'border-spa-border text-spa-text-secondary hover:bg-spa-muted'
                    }`}
                    data-testid={`trigger-${trigger.id}`}
                  >
                    <trigger.icon className="w-4 h-4" />
                    <span className="text-sm">{trigger.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Activities */}
            <div className="spa-card" data-testid="activities-card">
              <h2 className="font-serif text-xl text-spa-moss mb-4">Helpful activities</h2>
              <div className="flex flex-wrap gap-2">
                {ACTIVITY_OPTIONS.map((activity) => (
                  <button
                    key={activity.id}
                    onClick={() => toggleActivity(activity.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-colors ${
                      selectedActivities.includes(activity.id)
                        ? 'bg-spa-sage text-white border-spa-sage'
                        : 'border-spa-border text-spa-text-secondary hover:bg-spa-muted'
                    }`}
                    data-testid={`activity-${activity.id}`}
                  >
                    <activity.icon className="w-4 h-4" />
                    <span className="text-sm">{activity.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Journal */}
            <div className="spa-card" data-testid="journal-card">
              <h2 className="font-serif text-xl text-spa-moss mb-4">Journal Entry (Optional)</h2>
              <Textarea
                value={journalEntry}
                onChange={(e) => setJournalEntry(e.target.value)}
                placeholder="What's on your mind? Reflect on your day, gratitude, or anything you'd like to express..."
                className="spa-input min-h-[120px] resize-none"
                data-testid="journal-textarea"
              />
            </div>

            {/* Submit */}
            <Button 
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full spa-button-primary py-4"
              data-testid="save-mood-button"
            >
              {isSubmitting ? "Saving..." : "Save Mood Entry (+5 Coins)"}
            </Button>
          </div>

          {/* Sidebar Stats */}
          <div className="space-y-6">
            {/* Stats Card */}
            <div className="spa-card" data-testid="mood-stats-card">
              <h2 className="font-serif text-xl text-spa-moss mb-4">Your Stats</h2>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-spa-muted rounded-xl">
                  <span className="text-spa-text-secondary">30-Day Average</span>
                  <span className="font-serif text-2xl text-spa-moss">{stats?.avg_mood || "—"}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-spa-muted rounded-xl">
                  <span className="text-spa-text-secondary">Total Entries</span>
                  <span className="font-serif text-2xl text-spa-moss">{stats?.total_entries || 0}</span>
                </div>
              </div>
            </div>

            {/* Mini Chart */}
            {chartData.length > 0 && (
              <div className="spa-card" data-testid="mood-chart-card">
                <h2 className="font-serif text-xl text-spa-moss mb-4">Recent Trend</h2>
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      {CHART_GRADIENT}
                      <XAxis dataKey="day" axisLine={false} tickLine={false} tick={AXIS_TICK_STYLE} />
                      <YAxis domain={[0, 10]} hide />
                      <Tooltip />
                      <Area type="monotone" dataKey="mood" stroke="#C5705D" fill="url(#moodGrad)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Recent Entries */}
            <div className="spa-card" data-testid="recent-entries-card">
              <h2 className="font-serif text-xl text-spa-moss mb-4">Recent Entries</h2>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {entries.slice(0, 5).map((entry) => {
                  const MoodIcon = MOOD_OPTIONS.find(m => m.value >= entry.mood_score)?.icon || Meh;
                  return (
                    <div key={entry.entry_id} className="flex items-center gap-3 p-3 bg-spa-muted rounded-xl">
                      <MoodIcon className="w-5 h-5 text-spa-terracotta" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-spa-moss font-medium">{entry.mood_score}/10</p>
                        <p className="text-xs text-spa-text-secondary truncate">
                          {new Date(entry.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  );
                })}
                {entries.length === 0 && (
                  <p className="text-sm text-spa-text-secondary text-center py-4">
                    No entries yet. Start tracking!
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
